import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import * as Icons from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePublicCategories } from '../../../hooks/useCategories';

interface DiscoveryHeaderProps {
    config?: any;
}

export const DiscoveryHeader = ({ config }: DiscoveryHeaderProps) => {
    const { t, i18n } = useTranslation();
    const { data: fetchedCategories } = usePublicCategories();

    // Default constants designed for high conversion
    const DEFAULT_CATEGORIES = [
        { id: 'residential', label: t('explore.header.categories.residential', 'Residential'), icon: 'Home', link: '/search?category=residential' },
        { id: 'commercial', label: t('explore.header.categories.commercial', 'Commercial'), icon: 'Building2', link: '/search?category=commercial' },
        { id: 'coastal', label: t('explore.header.categories.coastal', 'Coastal'), icon: 'Store', link: '/search?category=coastal' },
    ];

    const DEFAULT_MOODS = [
        { id: 'ready', label: t('explore.header.moods.ready', 'Ready to Move'), link: '/search?ready=true' },
        { id: 'investment', label: t('explore.header.moods.investment', 'Investment Gems'), link: '/search?type=clinic' },
        { id: 'luxury', label: t('explore.header.moods.luxury', 'Luxury Villas'), link: '/search?type=villa' },
    ];

    const currentLang = i18n.language.startsWith('ar') ? 'ar' : 'en';

    // 1. Get Categories from Config (Array of IDs now) or Default to first 3
    let categoriesToDisplay: any[] = [];

    if (config?.categories && config.categories.length > 0 && fetchedCategories) {
        // Admin selected specific category IDs
        categoriesToDisplay = config.categories
            .map((id: string) => fetchedCategories.find((c: any) => c.id === id))
            .filter(Boolean); // Remove ones that might have been deleted
    } else if (fetchedCategories && fetchedCategories.length > 0) {
        // Fallback: Just show the first 3 categories
        categoriesToDisplay = fetchedCategories.slice(0, 3);
    } else {
        // Ultimate fallback
        categoriesToDisplay = DEFAULT_CATEGORIES;
    }

    const categories = categoriesToDisplay.map((cat: any) => {
        let label = typeof cat.label === 'string' ? cat.label : (cat.name?.[currentLang] || cat.label?.[currentLang]) || (cat.name?.en || cat.label?.en) || cat.slug || cat.label;

        // Custom translation mapping logic for known dynamic slugs
        if (cat.slug) {
            const cleanSlug = cat.slug.replace(/-/g, '');
            const translationKey = `propertyConfig.types.${cleanSlug}`;
            const translated = t(translationKey);
            if (translated && translated !== translationKey) {
                label = translated;
            } else if (currentLang === 'en' && typeof label === 'string') {
                label = label.charAt(0).toUpperCase() + label.slice(1).replace(/-/g, ' ');
            }
        }

        return {
            ...cat,
            label,
            icon: (Icons as any)[cat.icon] || Icons.Home,
            link: cat.link || `/search?sort=newest&category=${cat.id || cat.slug}`
        };
    });

    const moods = (config?.moods || DEFAULT_MOODS).map((mood: any) => ({
        ...mood,
        label: typeof mood.label === 'string' ? mood.label : (mood.label?.[currentLang] || mood.label?.en) || mood.label,
        link: mood.link || `/search?collection=${mood.id}`
    }));

    return (
        <div className="w-full bg-slate-50 border-b border-slate-200 sticky top-16 md:top-20 z-30 transition-all">
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 space-y-4">

                {/* 1. Header Title (Clean, Functional) */}
                <div className="flex items-center justify-between">
                    <h1 className="text-xl md:text-2xl font-bold text-slate-900 font-display">
                        {config?.title?.[currentLang] || t('explore.header.title', 'Discover Your Next Property')}
                    </h1>
                </div>

                {/* 2. Segmented Control */}
                <div className="bg-slate-200/50 p-1 rounded-lg flex items-center overflow-x-auto no-scrollbar">
                    {categories.map((cat: any) => {
                        const Icon = cat.icon;
                        return (
                            <Link
                                key={cat.id}
                                to={cat.link}
                                className={clsx(
                                    "flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all whitespace-nowrap",
                                    "text-slate-500 hover:text-primary hover:bg-white shadow-sm ring-1 ring-transparent hover:ring-black/5"
                                )}
                            >
                                <Icon className="w-4 h-4 text-slate-400 group-hover:text-primary" />
                                {cat.label}
                            </Link>
                        );
                    })}
                </div>

                {/* 3. Mood Filters (Chips) */}
                <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
                    {moods.map((mood: any) => {
                        return (
                            <Link
                                key={mood.id}
                                to={mood.link}
                                className={clsx(
                                    "px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors whitespace-nowrap",
                                    "bg-white border-slate-200 text-slate-600 hover:border-secondary hover:text-secondary"
                                )}
                            >
                                #{mood.label}
                            </Link>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
